<template>
  <nav class="navbar navbar-expand-lg navbar-dark fixed-top">
    <div class="container-fluid">
      <!-- Botao toggle sidebar -->
      <button class="sidebar-toggle-btn" @click="$emit('toggleSidebar')" aria-label="Toggle sidebar">
        <i class="fas fa-bars"></i>
      </button>

      <!-- Logo -->
      <router-link to="/home" class="navbar-brand logo-container">
        <img src="../assets/logo-sigel.png" class="logo-img" alt="SisGel Logo" />
      </router-link>

      <!-- Botao do menu responsivo -->
      <button
        ref="navbarTogglerRef"
        class="navbar-toggler custom-toggler"
        type="button"
        @click="toggleNavbarCollapse"
        aria-controls="navbarResponsive"
        :aria-expanded="navbarCollapseOpen"
        aria-label="Toggle navigation"
      >
        <span class="navbar-toggler-icon"></span>
      </button>

      <!-- Itens do menu -->
      <div
        class="navbar-collapse"
        :class="{ show: navbarCollapseOpen }"
        id="navbarResponsive"
        ref="navbarCollapseRef"
      >
        <span class="logo-text">
          Sistema de Gerenciamento de Loja - {{ nomeLoja }}
        </span>

        <!-- Area do usuario -->
        <div class="user-section">
          <!-- Loja selector -->
          <div v-if="lojasUsuario.length > 1" class="loja-selector">
            <i class="fas fa-store"></i>
            <SearchableSelect v-model="lojaSelecionada" @change="trocarLoja" class="form-select">
              <option :value="0">{{ $t('multiLoja.todasAsLojas') }}</option>
              <option v-for="loja in lojasUsuario" :key="loja.lojaId" :value="loja.lojaId">
                {{ loja.nome }}
              </option>
            </SearchableSelect>
          </div>

          <!-- Visualizacao removida — cards automatico no mobile -->

          <!-- Language Toggle (desativado por ora) -->
          <!--
          <button class="lang-toggle-btn" @click="toggleLocale" :title="currentLocale === 'pt-BR' ? 'Switch to English' : 'Mudar para Portugues'" aria-label="Alternar idioma">
            <i class="fas fa-globe"></i>
            <span class="lang-label">{{ currentLocale === 'pt-BR' ? 'EN - English' : 'PT - Portugues' }}</span>
          </button>
          -->

          <!-- Dark Mode Toggle -->
          <button class="theme-toggle-btn" @click="toggleTheme" :title="isDark ? 'Modo claro' : 'Modo escuro'" aria-label="Alternar tema">
            <i :class="isDark ? 'fas fa-sun' : 'fas fa-moon'"></i>
          </button>

          <!-- Notificacoes -->
          <div class="notification-container">
            <button class="notification-btn" @click="toggleNotifications" aria-label="Notificacoes">
              <i class="fas fa-bell"></i>
              <span v-if="notificationCount > 0" class="notification-badge">
                {{ notificationCount > 99 ? '99+' : notificationCount }}
              </span>
            </button>

            <!-- Dropdown: 3 ultimas + ver mais -->
            <div v-if="showNotifications" class="notification-dropdown">
              <div class="notification-header">
                <h6>{{ $t('notificacao.title') }}</h6>
                <button v-if="notificationCount > 0" @click="markAllAsRead" class="mark-all-read">
                  {{ $t('notificacao.marcarTodasLidas') }}
                </button>
              </div>
              <div class="notification-list">
                <div v-if="ultimasNotificacoes.length === 0" class="no-notifications">
                  <i class="fas fa-bell-slash"></i>
                  <span>{{ $t('notificacao.notFound') }}</span>
                </div>
                <div
                  v-for="n in ultimasNotificacoes"
                  :key="n.notificacaoId"
                  class="notification-item"
                  :class="{ unread: !n.lida }"
                >
                  <i :class="getNotificacaoIcon(n.tipo)" class="notification-icon"></i>
                  <div class="notification-content">
                    <div class="notification-title">{{ n.titulo }}</div>
                    <div class="notification-message">{{ n.mensagem }}</div>
                    <div class="notification-time">{{ formatarTempo(n.dataHora) }}</div>
                  </div>
                </div>
              </div>
              <div class="notification-footer">
                <router-link to="/notificacoes" class="ver-mais-link" @click="showNotifications = false">
                  <i class="fas fa-list"></i> {{ $t('common.details') }}
                </router-link>
              </div>
            </div>
          </div>

          <!-- Perfil do usuario -->
          <div class="user-profile">
            <div class="user-info">
              <div class="user-avatar"><i class="fas fa-user"></i></div>
              <div class="user-details">
                <span class="user-name">{{ authStore.getUser || "Usuario" }}</span>
                <span class="user-role">{{ userRole }}</span>
              </div>
            </div>
            <div class="user-dropdown">
              <button class="user-dropdown-btn" @click="toggleUserDropdown" aria-label="User menu">
                <i class="fas fa-chevron-down"></i>
              </button>
              <div v-if="showUserDropdown" class="user-dropdown-menu">
                <router-link to="/meu-perfil" class="dropdown-item">
                  <i class="fas fa-user-edit"></i> {{ $t('nav.meuPerfil') }}
                </router-link>
                <div class="dropdown-divider"></div>
                <button @click="logout" class="dropdown-item logout-item">
                  <i class="fas fa-sign-out-alt"></i> {{ $t('common.close') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
// TODO F44: Navbar component is too large (~300 lines). Consider extracting notification panel,
// loja selector, and user dropdown into separate components.
defineEmits(['toggleSidebar']);
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/admin/authStore";
import { useTheme } from "@/composables/useTheme";
import { setLocale } from "@/i18n";
import { useI18n } from "vue-i18n";
import { useTableView } from "@/composables/useTableView";
import NotificacaoService from "@/services/admin/NotificacaoService";
import type { NotificacaoSistemaResponse } from "@/models/response/admin/NotificacaoSistemaResponse";
import api from "@/plugins/axios";
import SearchableSelect from '@/components/SearchableSelect.vue'

const { isDark, toggleTheme } = useTheme();
const { locale } = useI18n();
const currentLocale = computed(() => locale.value);
const toggleLocale = () => {
  const newLocale = locale.value === 'pt-BR' ? 'en' : 'pt-BR';
  setLocale(newLocale);
};

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

// Navbar collapse (right drawer)
const navbarCollapseOpen = ref(false);
const navbarCollapseRef = ref<HTMLElement | null>(null);
const navbarTogglerRef = ref<HTMLElement | null>(null);

const toggleNavbarCollapse = () => {
  navbarCollapseOpen.value = !navbarCollapseOpen.value;
};

const closeNavbarCollapse = () => {
  navbarCollapseOpen.value = false;
};

// Fechar ao navegar para outra pagina
watch(() => route.fullPath, () => {
  closeNavbarCollapse();
});

// State
const showNotifications = ref(false);
const showUserDropdown = ref(false);
const notificationCount = ref(0);
const nomeLoja = ref("");
const ultimasNotificacoes = ref<NotificacaoSistemaResponse[]>([]);
const lojasUsuario = ref<{ lojaId: number; nome: string }[]>([]);
const lojaSelecionada = ref<number | null>(authStore.lojaId);
const { visualizacaoTabela, setView } = useTableView();
const visualizacaoAtual = ref(visualizacaoTabela.value);

// Computed
const userRole = computed(() => {
  const roles = authStore.getRoles || [];
  if (roles.includes("Administrador")) return "Administrador";
  if (roles.includes("Gerente")) return "Gerente";
  if (roles.includes("Vendedor")) return "Vendedor";
  if (roles.includes("Operador de Caixa")) return "Operador de Caixa";
  if (roles.includes("Controlador de Estoque")) return "Controlador de Estoque";
  return roles[0] || "Usuario";
});

// Notificacoes
const carregarNotificacoes = async () => {
  try {
    const page = await NotificacaoService.findAll({ page: 0, size: 3, sort: 'dataHora,desc' });
    ultimasNotificacoes.value = page?.content || [];
    try {
      notificationCount.value = await NotificacaoService.contarNaoLidas();
    } catch (e) {
      console.error('[Navbar] Erro ao contar notificacoes nao lidas:', e)
      notificationCount.value = ultimasNotificacoes.value.filter(n => !n.lida).length;
    }
  } catch (e) {
    console.error('[Navbar] Erro ao carregar notificacoes:', e)
    ultimasNotificacoes.value = [];
    notificationCount.value = 0;
  }
};

const getNotificacaoIcon = (tipo: string): string => {
  const map: Record<string, string> = {
    ESTOQUE: 'fas fa-boxes',
    VENDA: 'fas fa-shopping-cart',
    ENTREGA: 'fas fa-shipping-fast',
    FINANCEIRO: 'fas fa-dollar-sign',
    SISTEMA: 'fas fa-cog',
    TROCA_REGISTRADA: 'fas fa-exchange-alt',
    PARCELA_VENCIDA: 'fas fa-exclamation-triangle',
  };
  return map[tipo] || 'fas fa-bell';
};

const formatarTempo = (dataHora: string): string => {
  if (!dataHora) return '';
  try {
    const data = new Date(dataHora);
    const agora = new Date();
    const diffMin = Math.floor((agora.getTime() - data.getTime()) / 60000);
    if (diffMin < 1) return 'Agora';
    if (diffMin < 60) return `${diffMin}min atras`;
    const diffHoras = Math.floor(diffMin / 60);
    if (diffHoras < 24) return `${diffHoras}h atras`;
    const diffDias = Math.floor(diffHoras / 24);
    if (diffDias < 7) return `${diffDias}d atras`;
    return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch (e) { console.error('[Navbar] Erro ao formatar tempo:', e); return dataHora; }
};

// Loja
const carregarLojas = async () => {
  try {
    const { data } = await api.get<{ lojaId: number; nome: string }[]>('/usuario/lojas');
    lojasUsuario.value = data;
  } catch (e) {
    console.error('[Navbar] Erro ao carregar lojas:', e);
    lojasUsuario.value = [];
  }
};

const trocarLoja = () => {
  if (lojaSelecionada.value === 0) {
    router.push({ name: 'DashboardMultiLoja' });
    return;
  }
  if (lojaSelecionada.value) {
    authStore.setLojaId(lojaSelecionada.value);
    const loja = lojasUsuario.value.find(l => l.lojaId === lojaSelecionada.value);
    if (loja) authStore.setNomeLoja(loja.nome);
    window.location.reload();
  }
};

// Methods
const logout = () => { authStore.logout(); router.push({ name: "Login" }); };

const toggleNotifications = () => {
  showNotifications.value = !showNotifications.value;
  showUserDropdown.value = false;
  if (showNotifications.value) carregarNotificacoes();
};

const toggleUserDropdown = () => {
  showUserDropdown.value = !showUserDropdown.value;
  showNotifications.value = false;
};

const markAllAsRead = async () => {
  try {
    await NotificacaoService.marcarTodasComoLidas();
    notificationCount.value = 0;
    ultimasNotificacoes.value.forEach(n => { n.lida = true; });
  } catch (e) { console.error('[Navbar] Erro ao marcar notificacoes como lidas:', e) }
};

const alterarVisualizacao = () => {
  setView(visualizacaoAtual.value);
  // Keep DOM event for views that still listen via window events
  window.dispatchEvent(new CustomEvent("alterar-visualizacao-tabela", { detail: visualizacaoAtual.value }));
};

const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement;
  if (!target.closest(".notification-container") && !target.closest(".user-profile") && !target.closest(".view-style-dropdown")) {
    showNotifications.value = false;
    showUserDropdown.value = false;
  }
  // Fechar navbar collapse ao clicar fora
  if (navbarCollapseOpen.value && !target.closest("#navbarResponsive") && !target.closest(".navbar-toggler")) {
    closeNavbarCollapse();
  }
};

// Lifecycle
let pollInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  nomeLoja.value = authStore.nomeLoja;
  carregarNotificacoes();
  carregarLojas();
  pollInterval = setInterval(carregarNotificacoes, 60000);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (pollInterval) clearInterval(pollInterval);
});
</script>

<style scoped>
/* Navbar collapse controlado por Vue (sem Bootstrap JS) */
.navbar-collapse {
  display: flex;
  flex-basis: auto;
  flex-grow: 1;
  align-items: center;
}

@media (max-width: 991px) {
  .navbar-collapse {
    display: none;
    width: 100%;
    flex-basis: 100%;
  }
  .navbar-collapse.show {
    display: block;
  }
}

.sidebar-toggle-btn { background: none; border: none; color: #fff; font-size: 1.3rem; cursor: pointer; margin-right: 0.5rem; padding: 0.25rem 0.5rem; }
.sidebar-toggle-btn:hover { opacity: 0.8; }

@media (max-width: 640px) { .view-style-dropdown { display: none !important; } }
.view-style-dropdown { margin-right: 1rem; color: #fff; }
.view-style-dropdown select { background-color: #613178; color: #fff; border: none; border-radius: 6px; padding: 5px 10px; font-weight: 500; cursor: pointer; }
.view-style-dropdown select:hover { background-color: #8b5a96; }

.navbar-brand img.logo-img { height: 40px; }
.logo-text { margin-left: 1rem; font-weight: bold; color: #fff; }

/* Notificacoes */
.notification-container { position: relative; margin-right: 1rem; }
.notification-btn { background: none; border: none; color: #fff; position: relative; font-size: 1.2rem; cursor: pointer; min-width: 40px; min-height: 40px; }
.notification-badge { position: absolute; top: -5px; right: -10px; background-color: #d82a76; color: #fff; border-radius: 50%; padding: 2px 6px; font-size: 0.7rem; min-width: 18px; text-align: center; }

.notification-dropdown { position: absolute; top: calc(100% + 8px); right: 0; width: 360px; background: var(--bg-card, #fff); border-radius: var(--radius-lg, 12px); box-shadow: var(--shadow-xl, 0 8px 32px rgba(0,0,0,0.15)); z-index: 1040; overflow: hidden; animation: dropIn 0.25s ease; border: 1px solid var(--border-light, #e8e0e8); }
@keyframes dropIn { from { opacity: 0; transform: translateY(-8px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }

.notification-header { padding: 14px 16px; border-bottom: 1px solid var(--border-light); display: flex; justify-content: space-between; align-items: center; }
.notification-header h6 { margin: 0; color: var(--primary-color); font-weight: 700; font-size: 0.95rem; }
.mark-all-read { background: none; border: none; color: var(--accent-color); font-size: 0.75rem; cursor: pointer; font-weight: 600; }
.mark-all-read:hover { text-decoration: underline; }

.notification-list { max-height: 280px; overflow-y: auto; }
.no-notifications { padding: 28px; text-align: center; color: var(--text-secondary, #999); font-size: 0.9rem; display: flex; flex-direction: column; align-items: center; gap: 8px; }
.no-notifications i { font-size: 1.5rem; opacity: 0.5; }

.notification-item { padding: 12px 16px; border-bottom: 1px solid rgba(0,0,0,0.04); display: flex; align-items: flex-start; gap: 10px; transition: all 0.2s; }
.notification-item:hover { background-color: rgba(97,49,120,0.04); }
.notification-item.unread { background-color: rgba(97,49,120,0.06); border-left: 3px solid var(--primary-color); }
.notification-icon { color: var(--primary-color); font-size: 1rem; margin-top: 2px; min-width: 20px; text-align: center; }
.notification-content { flex: 1; min-width: 0; }
.notification-title { font-weight: 600; color: var(--text-dark); margin-bottom: 2px; font-size: 0.85rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.notification-message { color: var(--text-secondary); font-size: 0.78rem; margin-bottom: 2px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.notification-time { color: #aaa; font-size: 0.72rem; }

.notification-footer { padding: 10px 16px; border-top: 1px solid var(--border-light); text-align: center; }
.ver-mais-link { color: var(--primary-color); font-size: 0.85rem; font-weight: 600; text-decoration: none; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 4px; border-radius: 6px; transition: all 0.2s; }
.ver-mais-link:hover { background: rgba(97,49,120,0.06); }

/* User profile */
.user-profile { display: flex; align-items: center; position: relative; }
.user-avatar i { font-size: 1.5rem; color: #fff; }
.user-details { margin-left: 0.5rem; display: flex; flex-direction: column; color: #fff; }
.user-dropdown-btn { background: none; border: none; color: #fff; margin-left: 0.5rem; cursor: pointer; }
.user-dropdown-menu { position: absolute; top: 100%; right: 0; background-color: var(--bg-card, #fff); color: var(--text-dark, #000); border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.2); width: 200px; z-index: 100; }
.dropdown-item { padding: 0.5rem 1rem; cursor: pointer; display: flex; align-items: center; color: var(--text-dark, #333); text-decoration: none; }
.dropdown-item i { margin-right: 0.5rem; }
.dropdown-item:hover { background-color: rgba(97,49,120,0.06); }

/* Loja selector */
.loja-selector { display: flex; align-items: center; gap: 6px; margin-right: 1rem; color: #fff; }
.loja-selector select { background-color: #613178; color: #fff; border: none; border-radius: 6px; padding: 5px 10px; font-weight: 500; cursor: pointer; }
.loja-selector select:hover { background-color: #8b5a96; }

/* ===== MOBILE ===== */
@media (max-width: 991px) {
  .navbar-collapse {
    background: linear-gradient(135deg, #613178 0%, #4a2060 100%);
    border-radius: 0 0 12px 12px;
    padding: 16px;
    margin-top: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  }

  .logo-text {
    display: block;
    margin: 0 0 12px 0;
    font-size: 0.85rem;
    opacity: 0.9;
  }

  .user-section {
    flex-direction: column;
    align-items: stretch !important;
    gap: 12px;
  }

  .notification-container {
    margin-right: 0;
  }

  .notification-dropdown {
    position: fixed;
    top: 70px;
    left: 8px;
    right: 8px;
    width: auto;
  }

  .user-profile {
    padding: 10px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
  }

  .user-dropdown-menu {
    position: static;
    width: 100%;
    margin-top: 8px;
    background: rgba(255, 255, 255, 0.1);
    box-shadow: none;
    border-radius: 8px;
  }

  .user-dropdown-menu .dropdown-item {
    color: #fff;
    padding: 10px 16px;
  }

  .user-dropdown-menu .dropdown-item:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  .user-dropdown-menu .dropdown-divider {
    border-color: rgba(255, 255, 255, 0.15);
  }

  .loja-selector {
    margin-right: 0;
    width: 100%;
  }

  .loja-selector select {
    flex: 1;
  }

  .lang-toggle-btn,
  .theme-toggle-btn {
    padding: 8px 12px;
  }
}
</style>
